import { useEffect, useRef, useState } from "react";

export default function useCropTool({
  imgEl,
  imgSize,
  adjustments,
  canvasRef,
  setImgEl,
  setImgSize,
  setObjects,
  setSelectedIds,
  setAdjustments,
  clearHistory,
  setTool,
  attachGlobalDrag,
  detachGlobalDrag,
}) {
  const [showCrop, setShowCrop] = useState(false);
  const [cropBox, setCropBox] = useState({ x: 50, y: 50, w: 600, h: 400 });
  const cropDrag = useRef(null);
  const cropCanvasRef = useRef(null);

  function getDefaultCropBox() {
    return {
      x: imgSize.w * 0.1,
      y: imgSize.h * 0.1,
      w: imgSize.w * 0.8,
      h: imgSize.h * 0.8,
    };
  }

  function cancelCrop() {
    setShowCrop(false);
    setTool("cursor");
  }

  function openCrop() {
    if (showCrop) {
      cancelCrop();
    } else {
      setShowCrop(true);
      setTool("crop");
      setCropBox(getDefaultCropBox());
    }
  }

  function resetCrop() {
    setCropBox(getDefaultCropBox());
  }

  function doneCrop() {
    if (!imgEl) {
      setShowCrop(false);
      setTool("cursor");
      return;
    }

    const rawW = imgEl.naturalWidth, rawH = imgEl.naturalHeight;
    const scale = rawW / (imgSize.w || 1);

    const rcw = Math.max(1, Math.round(cropBox.w * scale));
    const rch = Math.max(1, Math.round(cropBox.h * scale));
    const rcx = Math.round(cropBox.x * scale);
    const rcy = Math.round(cropBox.y * scale);

    const off = document.createElement("canvas");
    off.width = rcw;
    off.height = rch;
    const ctx = off.getContext("2d");

    ctx.filter = `brightness(${adjustments.brightness}%) contrast(${adjustments.contrast}%) saturate(${adjustments.saturation}%)`;
    ctx.drawImage(imgEl, -rcx, -rcy, rawW, rawH);
    ctx.filter = "none";

    const shiftPt = (p) => ({ x: p.x - cropBox.x, y: p.y - cropBox.y });
    setObjects(os => os.map(o => {
      const no = { ...o };
      if (no.x != null) { const s = shiftPt({ x: no.x, y: no.y }); no.x = s.x; no.y = s.y; }
      if (no.x1 != null) { const s1 = shiftPt({ x: no.x1, y: no.y1 }); no.x1 = s1.x; no.y1 = s1.y; }
      if (no.x2 != null) { const s2 = shiftPt({ x: no.x2, y: no.y2 }); no.x2 = s2.x; no.y2 = s2.y; }
      if (no.anchorX != null) {
        const a = shiftPt({ x: no.anchorX, y: no.anchorY }); no.anchorX = a.x; no.anchorY = a.y;
        const t = shiftPt({ x: no.targetX, y: no.targetY }); no.targetX = t.x; no.targetY = t.y;
        const l = shiftPt({ x: no.lensX, y: no.lensY }); no.lensX = l.x; no.lensY = l.y;
      }
      if (no.points) no.points = no.points.map(shiftPt);
      return no;
    }));

    const cw = Math.max(1, Math.round(cropBox.w));
    const ch = Math.max(1, Math.round(cropBox.h));

    const newImg = new window.Image();
    newImg.onload = () => {
      setImgEl(newImg);
      setImgSize({ w: cw, h: ch });
      setSelectedIds([]);
      clearHistory();
      setAdjustments({ brightness: 100, contrast: 100, saturation: 100 });
    };
    newImg.src = off.toDataURL("image/jpeg", 0.95);
    setShowCrop(false);
    setTool("cursor");
  }

  function onCropDown(e) {
    if (!showCrop) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const sx = canvasRef.current.width / rect.width;
    const sy = canvasRef.current.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    const pt = {
      x: (src.clientX - rect.left) * sx,
      y: (src.clientY - rect.top) * sy,
    };
    const b = cropBox;
    const margin = 16;
    let handle = null;

    if (Math.abs(pt.x - b.x) < margin && Math.abs(pt.y - b.y) < margin) handle = "tl";
    else if (Math.abs(pt.x - (b.x + b.w)) < margin && Math.abs(pt.y - b.y) < margin) handle = "tr";
    else if (Math.abs(pt.x - b.x) < margin && Math.abs(pt.y - (b.y + b.h)) < margin) handle = "bl";
    else if (Math.abs(pt.x - (b.x + b.w)) < margin && Math.abs(pt.y - (b.y + b.h)) < margin) handle = "br";
    else if (pt.x >= b.x && pt.x <= b.x + b.w && pt.y >= b.y && pt.y <= b.y + b.h) handle = "move";

    if (handle) {
      cropDrag.current = { handle, startPt: pt, snapBox: { ...b } };
      if (!e.touches) attachGlobalDrag(onCropMove, onCropUp);
    }
  }

  function onCropMove(e) {
    if (!cropDrag.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const sx = canvasRef.current.width / rect.width;
    const sy = canvasRef.current.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    const pt = {
      x: (src.clientX - rect.left) * sx,
      y: (src.clientY - rect.top) * sy,
    };
    const { handle, startPt: sp, snapBox: sb } = cropDrag.current;
    const dx = pt.x - sp.x;
    const dy = pt.y - sp.y;

    setCropBox(b => {
      const nb = { ...b };
      if (handle === "move") { nb.x = sb.x + dx; nb.y = sb.y + dy; }
      else if (handle === "tl") { nb.x = sb.x + dx; nb.y = sb.y + dy; nb.w = sb.w - dx; nb.h = sb.h - dy; }
      else if (handle === "tr") { nb.w = sb.w + dx; nb.y = sb.y + dy; nb.h = sb.h - dy; }
      else if (handle === "bl") { nb.x = sb.x + dx; nb.w = sb.w - dx; nb.h = sb.h + dy; }
      else if (handle === "br") { nb.w = sb.w + dx; nb.h = sb.h + dy; }
      return nb;
    });
  }

  function onCropUp() {
    cropDrag.current = null;
    detachGlobalDrag();
  }

  useEffect(() => {
    if (!showCrop || !cropCanvasRef.current) return;

    const cc = cropCanvasRef.current;
    const ctx = cc.getContext("2d");
    ctx.clearRect(0, 0, cc.width, cc.height);
    ctx.fillStyle = "rgba(0,0,0,0.55)";
    ctx.fillRect(0, 0, cc.width, cc.height);
    ctx.clearRect(cropBox.x, cropBox.y, cropBox.w, cropBox.h);
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.lineWidth = 0.8;

    for (let i = 1; i < 3; i++) {
      ctx.beginPath();
      ctx.moveTo(cropBox.x + cropBox.w * i / 3, cropBox.y);
      ctx.lineTo(cropBox.x + cropBox.w * i / 3, cropBox.y + cropBox.h);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(cropBox.x, cropBox.y + cropBox.h * i / 3);
      ctx.lineTo(cropBox.x + cropBox.w, cropBox.y + cropBox.h * i / 3);
      ctx.stroke();
    }

    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.strokeRect(cropBox.x, cropBox.y, cropBox.w, cropBox.h);
    const hs = 12;
    [
      [cropBox.x, cropBox.y],
      [cropBox.x + cropBox.w - hs, cropBox.y],
      [cropBox.x, cropBox.y + cropBox.h - hs],
      [cropBox.x + cropBox.w - hs, cropBox.y + cropBox.h - hs],
    ].forEach(([cx, cy]) => {
      ctx.fillStyle = "#fff";
      ctx.fillRect(cx, cy, hs, hs);
    });
  }, [showCrop, cropBox]);

  return {
    cropCanvasRef,
    cropBox,
    showCrop,
    setShowCrop,
    openCrop,
    resetCrop,
    doneCrop,
    cancelCrop,
    onCropDown,
    onCropMove,
    onCropUp,
  };
}

